import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { LucideIconPicker } from './LucideIconPicker';
import { Save, Loader2, Eye, EyeOff, Gift, UserCircle, Plus } from 'lucide-react';
import type { Badge as BadgeType } from '@shared/schema';

const BADGE_CATEGORIES_OPTIONS = [
  { value: 'loyalty',          label: 'Loyalty' },
  { value: 'kudoz',            label: 'Kudoz Earned' },
  { value: 'review',           label: 'Reviews' },
  { value: 'engagement',       label: 'Engagement' },
  { value: 'social',           label: 'Social' },
  { value: 'order_diversity',  label: 'Order Diversity' },
  { value: 'review_diversity', label: 'Review Diversity' },
  { value: 'profile',          label: 'Profile' },
  { value: 'trust',            label: 'Trust' },
  { value: 'tip',              label: 'Tip Generosity' },
  { value: 'community',        label: 'Community' },
];

const TIER_OPTIONS = [
  { value: 'bronze',   label: 'Bronze' },
  { value: 'silver',   label: 'Silver' },
  { value: 'gold',     label: 'Gold' },
  { value: 'platinum', label: 'Platinum' },
];

const REQUIREMENT_TYPE_OPTIONS = [
  { value: 'reviews_count',              label: 'Reviews Count' },
  { value: 'unique_restaurants_reviewed',label: 'Unique Restaurants Reviewed' },
  { value: 'unique_restaurants_ordered', label: 'Unique Restaurants Ordered' },
  { value: 'restaurant_orders',          label: 'Orders at a Single Restaurant' },
  { value: 'lifetime_kudoz_earned',      label: 'Lifetime Kudoz Earned' },
  { value: 'friends_count',             label: 'Friends Count' },
  { value: 'followers_count',           label: 'Followers Count' },
  { value: 'app_sessions_count',        label: 'App Sessions Count' },
  { value: 'profile_completion_percent',label: 'Profile Completion %' },
  { value: 'trust_score',               label: 'Trust Score' },
  { value: 'average_tip_percent',       label: 'Average Tip %' },
];

const badgeFormSchema = z.object({
  category:        z.string().min(1, 'Category is required'),
  tier:            z.string().min(1, 'Tier is required'),
  requirementType: z.string().min(1, 'Requirement type is required'),
  name:            z.string().min(1, 'Badge name is required').max(100),
  description:     z.string().min(1, 'Description is required').max(500),
  iconName:        z.string().min(1, 'Icon is required'),
  thresholdValue:  z.coerce.number().int().min(0),
  bonusKudoz:      z.coerce.number().int().min(0),
  isActive:        z.boolean(),
  displayInProfile:z.boolean(),
  isHidden:        z.boolean(),
});

type BadgeFormData = z.infer<typeof badgeFormSchema>;

interface BadgeEditDialogProps {
  badge: BadgeType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingBadges?: BadgeType[];
}

export function BadgeEditDialog({ badge, open, onOpenChange, existingBadges = [] }: BadgeEditDialogProps) {
  const { toast } = useToast();
  const isCreate = !badge;

  const form = useForm<BadgeFormData>({
    resolver: zodResolver(badgeFormSchema),
    defaultValues: {
      category:         'loyalty',
      tier:             'bronze',
      requirementType:  'reviews_count',
      name:             '',
      description:      '',
      iconName:         'Award',
      thresholdValue:   1,
      bonusKudoz:       0,
      isActive:         true,
      displayInProfile: true,
      isHidden:         false,
    },
  });

  useEffect(() => {
    if (badge) {
      form.reset({
        category:         badge.category,
        tier:             badge.tier,
        requirementType:  badge.requirementType,
        name:             badge.name,
        description:      badge.description,
        iconName:         badge.iconName,
        thresholdValue:   badge.thresholdValue,
        bonusKudoz:       badge.bonusKudoz ?? 0,
        isActive:         badge.isActive ?? true,
        displayInProfile: badge.displayInProfile ?? true,
        isHidden:         badge.isHidden ?? false,
      });
    } else {
      form.reset({
        category:         'loyalty',
        tier:             'bronze',
        requirementType:  'reviews_count',
        name:             '',
        description:      '',
        iconName:         'Award',
        thresholdValue:   1,
        bonusKudoz:       0,
        isActive:         true,
        displayInProfile: true,
        isHidden:         false,
      });
    }
  }, [badge, open, form]);

  const selectedCategory = form.watch('category');
  const selectedTier = form.watch('tier');
  const slotTaken = isCreate && existingBadges.some(
    b => b.category === selectedCategory && b.tier === selectedTier
  );

  const updateMutation = useMutation({
    mutationFn: async (data: BadgeFormData) => {
      if (!badge) throw new Error('No badge selected');
      return apiRequest('PATCH', `/api/admin/badges/${badge.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/badges'] });
      onOpenChange(false);
      toast({ title: 'Badge updated', description: `"${form.getValues('name')}" has been saved.` });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update badge', variant: 'destructive' });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: BadgeFormData) => {
      return apiRequest('POST', '/api/admin/badges', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/badges'] });
      onOpenChange(false);
      toast({ title: 'Badge created', description: `"${form.getValues('name')}" has been added.` });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create badge', variant: 'destructive' });
    },
  });

  const isPending = updateMutation.isPending || createMutation.isPending;

  const onSubmit = (data: BadgeFormData) => {
    if (isCreate) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'bronze':   return 'bg-amber-600';
      case 'silver':   return 'bg-gray-400';
      case 'gold':     return 'bg-yellow-500';
      case 'platinum': return 'bg-purple-500';
      default:         return 'bg-gray-500';
    }
  };

  const getCategoryLabel = (category?: string) =>
    BADGE_CATEGORIES_OPTIONS.find(c => c.value === category)?.label || category || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCreate ? (
              <>
                <Plus className="w-4 h-4" />
                New Badge
              </>
            ) : (
              <>
                Edit Badge
                <Badge className={getTierColor(badge?.tier)}>{badge?.tier}</Badge>
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isCreate
              ? 'Create a new badge for a category and tier that does not already have one.'
              : `Category: ${getCategoryLabel(badge?.category)} | Requirement: ${badge?.requirementType}`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

            {isCreate && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BADGE_CATEGORIES_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-tier">
                            <SelectValue placeholder="Select tier" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIER_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {slotTaken && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                A {selectedTier} badge for {getCategoryLabel(selectedCategory)} already exists. Choose a different category or tier.
              </p>
            )}

            {isCreate && (
              <FormField
                control={form.control}
                name="requirementType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Requirement Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-requirement-type">
                          <SelectValue placeholder="Select requirement" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {REQUIREMENT_TYPE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>The metric used to determine when this badge is earned</FormDescription>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Badge Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Food Critic" data-testid="input-badge-name" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe how to earn this badge..."
                      rows={2}
                      data-testid="input-badge-description"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="iconName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <LucideIconPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="thresholdValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Threshold Value</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min={0} data-testid="input-threshold" />
                    </FormControl>
                    <FormDescription>
                      {badge ? badge.requirementType.replace(/_/g, ' ') : form.watch('requirementType').replace(/_/g, ' ')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bonusKudoz"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bonus Kudoz</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min={0} data-testid="input-bonus-kudoz" />
                    </FormControl>
                    <FormDescription>Awarded when unlocked</FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <h4 className="font-medium text-sm">Visibility &amp; Status</h4>

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {field.value ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                      <div>
                        <FormLabel className="cursor-pointer">Active</FormLabel>
                        <FormDescription>Users can earn this badge</FormDescription>
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-is-active"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayInProfile"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4" />
                      <div>
                        <FormLabel className="cursor-pointer">Show in Profile</FormLabel>
                        <FormDescription>Display on user's public profile</FormDescription>
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-display-in-profile"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isHidden"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4" />
                      <div>
                        <FormLabel className="cursor-pointer">Hidden / Surprise Badge</FormLabel>
                        <FormDescription>Not promoted, but still awarded (e.g., tip generosity)</FormDescription>
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-is-hidden"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || slotTaken} data-testid="button-save-badge">
                {isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : isCreate ? (
                  <Plus className="w-4 h-4 mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isCreate ? 'Create Badge' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
